// TODO kk: refactor to separate hook files
import Cookies from "js-cookie"
import io, { Socket } from "socket.io-client"
import { authRedirectTargetUrl } from "@store/services/authQuery"
import { VariantType, enqueueSnackbar } from "notistack"
import {
  ClientToServerEvent,
  CursorPayload,
  GraphNewVersionDto,
  ServerToClientEvent,
  SocketCursorMessage,
  SocketGraphLockMessage,
  SocketJoinCursorRoom,
  SocketJoinEditRoom,
  SocketLeaveRoom,
  SocketLlmDescriptionUpdateDto,
  SocketLlmDescriptionUpdateMessage,
  SocketLocNodeMessage,
  SocketLockSuccessMessage,
  SocketRoomPrefix,
  SocketSubscriber,
} from "@features/builder/types"
import { EditorDisconnectActionButton } from "@features/builder/hooks/EditorDisconnectActionButton"
import logger from "@utils/logger"
import { atom, getDefaultStore } from "jotai"
import { BehaviorSubject } from "rxjs"
import { graphAtom } from "@features/builder/graph/graphAtoms"

export interface CursorRoomPayloadMembers {
  payload: CursorPayload[]
  members: SocketSubscriber[]
}

const buildSocketIoAuth = () => ({
  token: Cookies.get("User"),
  keep: import.meta.env.VITE_APP_EDITOR_MULTIPLE_SESSION === "true",
})

const socket: Socket<ServerToClientEvent, ClientToServerEvent> = io(
  import.meta.env.VITE_APP_EDITOR_URL,
  {
    transports: ["websocket"],
    autoConnect: false,
    auth: buildSocketIoAuth(),
  },
)

const roomNamesCache = new Set<string>()
const joiningRooms = new Set<string>()
const desiredCrdtGraphIds = new Set<string>()
const desiredCursorPayloadByGraphId = new Map<string, CursorPayload>()
const isSocketDebugEnabled = import.meta.env.VITE_APP_EDITOR_SOCKET_DEBUG === "true"
const socketJoinAckTimeoutMs = 10000

const logSocketState = (reason: string) => {
  if (!isSocketDebugEnabled) return
  logger.info(`[socket-debug] ${reason}`, {
    connected: socket.connected,
    socketId: socket.id,
    roomNamesCacheSize: roomNamesCache.size,
    joiningRoomsSize: joiningRooms.size,
    lockRoomsCount: getDefaultStore().get(lockMemberAtom).size,
    memberLockRoomsCount: getDefaultStore().get(memberLockAtom).size,
    cursorRoomsCount: cursorSubject.getValue().size,
  })
}

export interface EditRoomLocksMember {
  sid: string
  member: SocketSubscriber
  related: boolean
  lock: SocketLockSuccessMessage
}

export interface EditRoomMemberLocks {
  member: SocketSubscriber
  locks: SocketLocNodeMessage[]
  isPlaceholder: boolean
}

export const lockMemberAtom = atom(new Map<string, Map<string, EditRoomLocksMember>>())
export const memberLockAtom = atom(new Map<string, Map<string, EditRoomMemberLocks>>())

export const getRoomLocks = (roomId: string, nodeId: string): EditRoomLocksMember | undefined => {
  const room = getDefaultStore().get(lockMemberAtom).get(roomId)
  if (!room) return

  return room.get(nodeId)
}

export const getRoomMembers = (roomId: string): Map<string, EditRoomMemberLocks> | undefined => {
  return getDefaultStore().get(memberLockAtom).get(roomId)
}

export const isLocked = (lock: EditRoomLocksMember | undefined): boolean => {
  if (!lock) return false
  return lock.sid !== socket.id
}

export const isMyLock = (lock: EditRoomLocksMember | undefined): boolean => {
  if (!lock) return false
  return lock.sid === socket.id
}

const isSocketLockSuccessMessage = (
  lock: SocketLocNodeMessage,
): lock is SocketLockSuccessMessage => {
  return (
    typeof (lock as SocketLockSuccessMessage).dt === "number" &&
    Array.isArray((lock as SocketLockSuccessMessage).related_node_ids)
  )
}

const handleJoinCrdtRoom = (response: SocketJoinEditRoom): void => {
  const lockData = new Map<string, EditRoomLocksMember>()

  const memberData = new Map<string, EditRoomMemberLocks>()
  response.members.forEach((member) =>
    memberData.set(member.sid, { member, locks: [], isPlaceholder: false }),
  )

  response.locks.forEach((lock) =>
    pushMemberLock(lock as SocketLockSuccessMessage, memberData, lockData),
  )

  for (const [placeholderSid, placeholderEntry] of memberData.entries()) {
    if (!placeholderEntry.isPlaceholder) continue

    const realMemberEntry = Array.from(memberData.entries()).find(([sid, candidate]) => {
      if (sid === placeholderSid) return false
      return candidate.member.email.toLowerCase() === placeholderEntry.member.email.toLowerCase()
    })

    if (!realMemberEntry) continue
    const [realSid, realEntry] = realMemberEntry

    placeholderEntry.locks.forEach((lock) => {
      if (!isSocketLockSuccessMessage(lock)) return

      const lockWithResolvedSid: SocketLockSuccessMessage = { ...lock, sid: realSid }
      lockData.set(lock.node_id, {
        sid: realSid,
        lock: lockWithResolvedSid,
        member: realEntry.member,
        related: false,
      })
      lock.related_node_ids.forEach((id: string) =>
        lockData.set(id, {
          sid: realSid,
          lock: lockWithResolvedSid,
          member: realEntry.member,
          related: true,
        }),
      )
    })

    realEntry.locks.push(...placeholderEntry.locks.map((lock) => ({ ...lock, sid: realSid })))
    memberData.delete(placeholderSid)
  }

  const lockMember = getDefaultStore().get(lockMemberAtom)
  const memberLock = getDefaultStore().get(memberLockAtom)
  lockMember.set(response.room_name, lockData)
  memberLock.set(response.room_name, memberData)
  getDefaultStore().set(lockMemberAtom, new Map(lockMember))
  getDefaultStore().set(memberLockAtom, new Map(memberLock))

  const isOwnEvent = response.sid === socket.id
  if (isOwnEvent) {
    getDefaultStore().set(graphAtom, {
      graphId: response.room_name.replace(`${SocketRoomPrefix.CRDT}-`, ""),
      isLocked: true,
      remotelyLockedBy: response.graph_lock?.email ?? null,
      isRemotelyLockedBySomeoneElse: response.graph_lock
        ? response.graph_lock.sid !== socket.id
        : false,
    })
  }
}

const pushMemberLock = (
  lock: SocketLockSuccessMessage,
  memberData: Map<string, EditRoomMemberLocks>,
  lockData: Map<string, EditRoomLocksMember>,
) => {
  let memberLock = memberData.get(lock.sid)
  if (!memberLock) {
    // Lock events can arrive before join data contains the full member.
    // Create a temporary member from lock payload and reconcile it later.
    memberLock = {
      member: {
        sid: lock.sid,
        email: lock.email,
        first_name: "",
        last_name: "",
        name: lock.email,
      },
      locks: [],
      isPlaceholder: true,
    }
    memberData.set(lock.sid, memberLock)
    logger.warn(`Member not found, creating fallback ${lock.node_id}`)
  }

  lockData.set(lock.node_id, {
    sid: lock.sid,
    lock: lock,
    member: memberLock.member,
    related: false,
  })
  lock.related_node_ids.forEach((id) =>
    lockData.set(id, { sid: lock.sid, lock: lock, related: true, member: memberLock.member }),
  )

  memberLock.locks.push(lock)
}

const cleanMemberLock = (
  lock: SocketLockSuccessMessage,
  memberData: Map<string, EditRoomMemberLocks>,
  lockData: Map<string, EditRoomLocksMember>,
) => {
  const memberLock = memberData.get(lock.sid)
  lockData.delete(lock.node_id)
  lock.related_node_ids.forEach((id) => lockData.delete(id))

  if (!memberLock) {
    logger.warn(`Member not found on release ${lock.node_id}`)
    return
  }

  memberLock.locks = memberLock.locks.filter((l) => l.node_id !== lock.node_id)
  if (memberLock.locks.length === 0) {
    memberData.delete(lock.sid)
  }
}

const handleNodeLockUpdate = (response: SocketLocNodeMessage): void => {
  const lockMember = getDefaultStore().get(lockMemberAtom)
  const memberLock = getDefaultStore().get(memberLockAtom)
  const roomName = `${SocketRoomPrefix.CRDT}-${response.graph_id}`
  const lockData = lockMember.get(roomName) ?? new Map<string, EditRoomLocksMember>()
  const memberData = memberLock.get(roomName) ?? new Map<string, EditRoomMemberLocks>()
  if (!lockMember.has(roomName) || !memberLock.has(roomName)) {
    logger.warn(`handleLockUpdate room not initialized, creating ${roomName}`)
  }
  switch (response.type) {
    case "acquire": {
      pushMemberLock(response as SocketLockSuccessMessage, memberData, lockData)
      lockMember.set(roomName, structuredClone(lockData))
      memberLock.set(roomName, structuredClone(memberData))
      getDefaultStore().set(lockMemberAtom, new Map(lockMember))
      getDefaultStore().set(memberLockAtom, new Map(memberLock))
      break
    }
    case "release": {
      cleanMemberLock(response as SocketLockSuccessMessage, memberData, lockData)
      lockMember.set(roomName, structuredClone(lockData))
      memberLock.set(roomName, structuredClone(memberData))
      getDefaultStore().set(lockMemberAtom, new Map(lockMember))
      getDefaultStore().set(memberLockAtom, new Map(memberLock))
      break
    }
    case "error": {
      break
    }
  }
}

const cursorSubject = new BehaviorSubject<Map<string, CursorRoomPayloadMembers>>(new Map())
export const cursorObserver = cursorSubject.asObservable()
export const getCursorRoomData = (roomName: string): CursorRoomPayloadMembers | undefined => {
  return cursorSubject.value.get(roomName)
}

const llmDescriptionSubject = new BehaviorSubject<Map<string, SocketLlmDescriptionUpdateDto>>(
  new Map(),
)
export const llmDescriptionObserver = llmDescriptionSubject.asObservable()
export const getLlmDescriptionByTaskId = (
  taskId?: string,
): SocketLlmDescriptionUpdateDto | undefined => {
  if (!taskId) return
  return llmDescriptionSubject.value.get(taskId)
}

export const clearLlmDescriptionTask = (taskId?: string): void => {
  if (!taskId) return

  const updates = llmDescriptionSubject.getValue()
  if (!updates.has(taskId)) return

  const next = new Map(updates)
  next.delete(taskId)
  llmDescriptionSubject.next(next)
}

const handleLlmDescriptionUpdate = (response: SocketLlmDescriptionUpdateMessage) => {
  const payload = "data" in response ? response.data : response
  if (!payload.task_id) return

  const updates = llmDescriptionSubject.getValue()
  updates.set(payload.task_id, payload)
  llmDescriptionSubject.next(new Map(updates))
}

const handleJoinCursorRoom = (response: SocketJoinCursorRoom) => {
  const rooms = cursorSubject.getValue()
  const room = rooms.get(response.room_name)
  if (room) {
    room.members = response.members
    room.payload = room.payload.filter((p) => p.sid !== response.sid)
    room.payload.push(response.cursor)
  } else {
    rooms.set(response.room_name, {
      payload: [response.cursor],
      members: response.members,
    })
  }
  cursorSubject.next(rooms)
}

const handleCursorUpdate = (response: SocketCursorMessage) => {
  const rooms = cursorSubject.getValue()
  const room = rooms.get(response.room_name)
  if (room) {
    room.payload = room.payload.filter((p) => p.sid !== response.sid)
    room.payload.push(response.cursor)
    rooms.set(response.room_name, structuredClone(room))
    cursorSubject.next(rooms)
  }
}

const handleLeaveRoom = (response: SocketLeaveRoom) => {
  if (response.room_name.startsWith(SocketRoomPrefix.CURSOR)) {
    return leaveCursorRoom(response)
  } else if (response.room_name.startsWith(SocketRoomPrefix.CRDT)) {
    return leaveLockRoom(response)
  }
}

const leaveLockRoom = (response: SocketLeaveRoom) => {
  const lockMember = getDefaultStore().get(lockMemberAtom)
  const memberLock = getDefaultStore().get(memberLockAtom)
  const lockMemberRoom = lockMember.get(response.room_name)
  const memberLockRoom = memberLock.get(response.room_name)
  if (!lockMemberRoom || !memberLockRoom) {
    logger.warn(`Room not found ${response.room_name}`)
    return
  }
  lockMemberRoom.forEach((value: EditRoomLocksMember, key: string) => {
    if (value.sid === response.sid) lockMemberRoom.delete(key)
  })
  memberLockRoom.forEach((value: EditRoomMemberLocks, key: string) => {
    if (value.member.sid === response.sid) memberLockRoom.delete(key)
  })
  getDefaultStore().set(lockMemberAtom, new Map(lockMember))
  getDefaultStore().set(memberLockAtom, new Map(memberLock))
}

const leaveCursorRoom = (response: SocketLeaveRoom) => {
  const rooms = cursorSubject.getValue()
  const room = rooms.get(response.room_name)
  if (room) {
    // TODO ms: simplify BE and FE to be more reactive send different messages for user room interactions
    room.members = room.members.filter((m) => m.sid !== response.sid)
    room.payload = room.payload.filter((p) => p.sid !== response.sid)

    rooms.set(response.room_name, structuredClone(room))
    cursorSubject.next(rooms)
    return
  }
}

// eslint-disable-next-line @typescript-eslint/no-explicit-any -- ANY :(
const handleConnectErrorInternal = (err: any) => {
  if (err.data?.status == 401) {
    authRedirectTargetUrl()
  }
}

const handleGraphNewVersion = (msg: GraphNewVersionDto) => {
  const url = new URL(window.location.href)
  url.searchParams.set("graphKey", msg.graph_id)
  window.location.assign(url)
}

const handleErrorInternal = (err: { status: number; message: string; variant?: VariantType }) => {
  logger.warn("socket.io error", err)
  if (err.status === 401) authRedirectTargetUrl()
  if (err.status === 402) {
    return enqueueSnackbar(err.message, {
      variant: "error",
      persist: true,
      action: EditorDisconnectActionButton,
    })
  }
  enqueueSnackbar(err.message, { variant: "error" })
}

const handleNotificationInternal = (msg: { message: string; variant: VariantType }) => {
  enqueueSnackbar(msg.message, { variant: msg.variant })
}

const rejoinKnownRooms = () => {
  desiredCrdtGraphIds.forEach((graphId) => {
    socketJoinCrdtRoom(graphId)
  })

  desiredCursorPayloadByGraphId.forEach((payload) => {
    socketJoinCursorRoom(payload)
  })
}

const handleGraphLock = (response: SocketGraphLockMessage) => {
  const graphState = getDefaultStore().get(graphAtom)

  // this event shouldn't be emitted by BE, but just in case, we log it and ignore it to avoid locking the wrong graph
  if (graphState.graphId !== response.graph_id) {
    logger.error(`handleGraphLock graphId mismatch ${graphState.graphId} !== ${response.graph_id}`)
    return
  }

  const isOwnEvent = response.sid === socket.id

  switch (response.type) {
    case "acquire": {
      getDefaultStore().set(graphAtom, {
        ...graphState,
        isLocked: isOwnEvent ? graphState.isLocked : true,
        remotelyLockedBy: response.email,
        isRemotelyLockedBySomeoneElse: !isOwnEvent,
      })
      break
    }
    case "release": {
      getDefaultStore().set(graphAtom, {
        ...graphState,
        remotelyLockedBy: null,
        isRemotelyLockedBySomeoneElse: false,
      })
      break
    }
    case "error": {
      logger.error(`Error of "graph_lock" socket message.`, response.message)
      break
    }
  }
}

// cursor room
socket.on("cursor_join", handleJoinCursorRoom)
socket.on("crdt_join", handleJoinCrdtRoom)
socket.on("lock", handleNodeLockUpdate)
socket.on("res_cursor", handleCursorUpdate)
socket.on("leave", handleLeaveRoom)
socket.on("llm_description_update", handleLlmDescriptionUpdate)
socket.on("graph_lock", handleGraphLock)

socket.on("error", handleErrorInternal)

socket.on("notification", handleNotificationInternal)
socket.on("connect_error", handleConnectErrorInternal)
socket.on("res_graph_new_version", handleGraphNewVersion)
socket.on("connect", () => {
  logSocketState("connect")
  rejoinKnownRooms()
})
socket.io.on("reconnect_attempt", (attempt) => {
  if (!isSocketDebugEnabled) return
  logger.info(`[socket-debug] reconnect attempt ${attempt}`)
})
socket.io.on("reconnect", (attempt) => {
  if (!isSocketDebugEnabled) return
  logger.info(`[socket-debug] reconnect success on attempt ${attempt}`)
})

export const useSocketIO = () => socket

export const socketJoinCrdtRoom = (graphId?: string) => {
  if (!graphId) {
    logger.warn("[socket-debug] socketJoinCrdtRoom skipped due to missing graphId")
    return
  }
  desiredCrdtGraphIds.add(graphId)
  const roomName = `${SocketRoomPrefix.CRDT}-${graphId}`
  if (!socket.connected) {
    logSocketState(`socketJoinCrdtRoom deferred until connect ${roomName}`)
    return
  }
  if (joiningRooms.has(roomName)) {
    logger.warn(`socketJoinCrdtRoom joining room has ${roomName}`)
    logSocketState(`socketJoinCrdtRoom blocked ${roomName}`)
    return
  }
  joiningRooms.add(roomName)
  logSocketState(`socketJoinCrdtRoom emitWithAck start ${roomName}`)
  socket
    .timeout(socketJoinAckTimeoutMs)
    .emitWithAck("crdt_join", { id: graphId })
    .then(() => {
      roomNamesCache.add(roomName)
      logSocketState(`socketJoinCrdtRoom emitWithAck success ${roomName}`)
    })
    .catch((err) => {
      logger.warn(`[socket-debug] socketJoinCrdtRoom emitWithAck failed ${roomName}`, err)
      logSocketState(`socketJoinCrdtRoom emitWithAck failed ${roomName}`)
    })
    .finally(() => {
      joiningRooms.delete(roomName)
    })
}

export const socketLeaveCrdtRoom = (graphId?: string) => {
  if (!graphId) {
    logger.warn("[socket-debug] socketLeaveCrdtRoom skipped due to missing graphId")
    return
  }
  desiredCrdtGraphIds.delete(graphId)
  const roomName = `${SocketRoomPrefix.CRDT}-${graphId}`
  if (joiningRooms.has(roomName)) {
    joiningRooms.delete(roomName)
  }
  socket
    .emitWithAck("crdt_leave", { id: graphId })
    .catch((err) => {
      logger.warn(`[socket-debug] socketLeaveCrdtRoom emitWithAck failed ${roomName}`, err)
    })
    .finally(() => {
      roomNamesCache.delete(roomName)
      const lockMember = getDefaultStore().get(lockMemberAtom)
      const memberLock = getDefaultStore().get(memberLockAtom)
      lockMember.delete(roomName)
      memberLock.delete(roomName)
      getDefaultStore().set(lockMemberAtom, new Map(lockMember))
      getDefaultStore().set(memberLockAtom, new Map(memberLock))
    })
}

export const socketJoinCursorRoom = (payload: CursorPayload) => {
  desiredCursorPayloadByGraphId.set(payload.id, payload)
  const roomName = `${SocketRoomPrefix.CURSOR}-${payload.id}`
  if (roomNamesCache.has(roomName)) {
    logSocketState(`socketJoinCursorRoom reused cache ${roomName}`)
    socket.emit("req_cursor", payload)
    return
  }
  socket
    .emitWithAck("req_cursor_join", payload)
    .then(() => {
      roomNamesCache.add(roomName)
      logSocketState(`socketJoinCursorRoom emitWithAck success ${roomName}`)
    })
    .catch((err) => {
      logger.warn(`[socket-debug] socketJoinCursorRoom emitWithAck failed ${roomName}`, err)
    })
}

export const socketLeaveCursorRoom = (graphId?: string) => {
  if (!graphId) return
  desiredCursorPayloadByGraphId.delete(graphId)
  socket
    .emitWithAck("req_cursor_leave", { id: graphId })
    .catch((err) => {
      logger.warn(`[socket-debug] socketLeaveCursorRoom emitWithAck failed ${graphId}`, err)
    })
    .finally(() => {
      const roomName = `${SocketRoomPrefix.CURSOR}-${graphId}`
      roomNamesCache.delete(roomName)
      const rooms = cursorSubject.getValue()
      rooms.delete(roomName)
      cursorSubject.next(rooms)
    })
}

export const socketLockRelease = (lock?: SocketLockSuccessMessage) => {
  if (!lock) return

  socket.emit("lock_release", {
    id: lock.graph_id,
    node_id: lock.node_id,
    related_node_ids: lock.related_node_ids,
  })
}

export const socketLockAcquire = (id: string, node_id: string, related_node_ids: string[]) => {
  socket.emit("lock_acquire", { id, node_id, related_node_ids })
}

const handleDisconnect = () => {
  roomNamesCache.clear()
  joiningRooms.clear()
  getDefaultStore().set(lockMemberAtom, new Map())
  getDefaultStore().set(memberLockAtom, new Map())
  cursorSubject.next(new Map())
  llmDescriptionSubject.next(new Map())
  logSocketState("disconnect")
}
socket.on("disconnect", handleDisconnect)
